import React from "react";
import { Component, ComponentType, IconLibrary, IconProperties } from "@/types";
import { iconLibraryService } from "@/services/icons/IconLibraryService";
import { useComponentInteraction } from "../InteractionContextRuntime";
import { toCssProperty, OBJECT_FIT_VALUES } from "./utils";

export interface MediaRendererProps {
  component: Component;
}

// ─────────────────────────────────────────────────────────────────────────────
// Image renderer
// ─────────────────────────────────────────────────────────────────────────────

const ImageRenderer: React.FC<{ component: Component }> = React.memo(
  ({ component }) => {
    const { properties = {}, style } = component;

    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{
          ...style,
          overflow: "hidden",
        }}
      >
        {typeof properties.src === "string" ? (
          <img
            src={properties.src}
            alt={typeof properties.alt === "string" ? properties.alt : "Image"}
            className="w-full h-full"
            style={{
              // Keeps objectFit narrowed to supported CSS values.
              objectFit: toCssProperty<"objectFit">(
                properties.objectFit,
                OBJECT_FIT_VALUES,
                "cover",
              ),
              opacity:
                typeof properties.opacity === "number"
                  ? properties.opacity
                  : 1,
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
            No image
          </div>
        )}
      </div>
    );
  },
);

ImageRenderer.displayName = "ImageRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// Icon renderer
// ─────────────────────────────────────────────────────────────────────────────

const IconRenderer: React.FC<{ component: Component }> = React.memo(
  ({ component }) => {
    const { properties = {}, style } = component;
    const [IconComponent, setIconComponent] = React.useState<React.ComponentType<{ size?: number; color?: string; strokeWidth?: number; style?: React.CSSProperties }> | null>(null);
    const [error, setError] = React.useState(false);
    const interaction = useComponentInteraction(component);

    const iconProps = properties as Partial<IconProperties> & {
      name?: unknown;
      icon?: unknown;
    };
    const iconName =
      typeof iconProps.iconName === "string"
        ? iconProps.iconName
        : typeof iconProps.name === "string"
        ? iconProps.name
        : typeof iconProps.icon === "string"
        ? iconProps.icon
        : "Star";
    const libraryValue =
      typeof iconProps.library === "string" ? iconProps.library : "lucide";
    const library =
      libraryValue === "custom"
        ? IconLibrary.CUSTOM
        : libraryValue === "radix"
        ? IconLibrary.RADIX
        : IconLibrary.LUCIDE;
    const size =
      typeof iconProps.size === "number"
        ? Math.min(Math.max(iconProps.size, 16), 128)
        : 24;
    const color =
      typeof iconProps.color === "string"
        ? iconProps.color
        : typeof style?.color === "string"
        ? style.color
        : "#000000";
    const strokeWidth =
      typeof iconProps.strokeWidth === "number" &&
      library === IconLibrary.LUCIDE
        ? Math.min(Math.max(iconProps.strokeWidth, 0.5), 4)
        : 2;
    const rotation =
      typeof iconProps.rotation === "number" ? iconProps.rotation % 360 : 0;
    const opacity =
      typeof iconProps.opacity === "number"
        ? Math.min(Math.max(iconProps.opacity, 0), 1)
        : 1;
    const label =
      typeof iconProps.label === "string"
        ? iconProps.label
        : `${iconName} icon`;

    const action =
      typeof iconProps.action === "string" ? iconProps.action : undefined;
    const target =
      typeof iconProps.target === "string" ? iconProps.target : undefined;
    const hasNavigation = action === "navigate" && !!target;

    const activateNavigation = async (event: { preventDefault: () => void; stopPropagation: () => void }) => {
      event.preventDefault();
      if (!interaction.canInteract || !interaction.handlers) return;
      if (action === "navigate" && interaction.handlers.onNavigate && target) {
        event.stopPropagation();
        await interaction.handlers.onNavigate({ targetPageId: target });
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      void activateNavigation(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        void activateNavigation(e);
      }
    };

    React.useEffect(() => {
      let mounted = true;
      const loadIcon = async () => {
        try {
          const loadedIcon = await iconLibraryService.loadIcon(
            iconName,
            library,
          );
          if (mounted) {
            if (loadedIcon) {
              setIconComponent(() => loadedIcon as React.ComponentType<{ size?: number; color?: string; strokeWidth?: number; style?: React.CSSProperties }>);
              setError(false);
            } else {
              setError(true);
            }
          }
        } catch (err) {
          console.error(`Failed to load icon ${iconName}:`, err);
          if (mounted) setError(true);
        }
      };
      loadIcon();
      return () => {
        mounted = false;
      };
    }, [iconName, library]);

    if (error || !IconComponent) {
      return (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ ...style, opacity }}
          role="img"
          aria-label={label}
        >
          <div
            className="flex items-center justify-center rounded-full bg-gray-100 text-gray-400"
            style={{ width: size, height: size, fontSize: size * 0.6 }}
          >
            ?
          </div>
        </div>
      );
    }

    const isClickable = hasNavigation && interaction.mode === "functional";

    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{ ...style, opacity, cursor: isClickable ? "pointer" : undefined }}
        role={isClickable ? "button" : "img"}
        aria-label={isClickable ? `${label} - Navigate` : label}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={isClickable ? handleKeyDown : undefined}
      >
        <div
          style={{
            transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconComponent
            size={size}
            color={color}
            strokeWidth={strokeWidth}
            style={{ width: size, height: size }}
          />
        </div>
      </div>
    );
  },
);

IconRenderer.displayName = "IconRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function MediaRenderers({ component }: MediaRendererProps): JSX.Element {
  if (component.type === ComponentType.IMAGE) {
    return <ImageRenderer component={component} />;
  }
  return <IconRenderer component={component} />;
}
